import { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus } from 'lucide-react';
import DropdownButton from '../Common/DropDown';
import SearchFloatingFilter from '../Common/searchFloatingFilter';
import { jiraColumnDefs } from '../AGColumns/JiraColumn';
import { getStatusColor, createColorMapping } from '../Common/standUpCommonFunctions';

function StatusBadges({ statusData, selectedStatuses, onToggle, colorMap }) {
  return (
    <>
      {statusData.map(({ status, count }, index) => {
        const isSelected = selectedStatuses.includes(status);
        const statusColor = getStatusColor(status, colorMap);

        return (
          <button
            type="button"
            key={`${status}-${index}`}
            onClick={() => onToggle(status)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md whitespace-nowrap text-sm font-medium transition-colors duration-200
              ${
                isSelected
                  ? 'bg-[#066FD1] text-white'
                  : 'bg-[#E8EEF4] text-[#24527A] dark:bg-[#293345] dark:text-[#E8EEF4]'
              }`}
            title={`${count} ${status}`}
          >
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusColor.tailwind}`}
              style={statusColor.hex ? { backgroundColor: statusColor.hex } : undefined}
            />
            <span>
              {count} {status}
            </span>
          </button>
        );
      })}
    </>
  );
}

StatusBadges.propTypes = {
  statusData: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  colorMap: PropTypes.instanceOf(Map).isRequired,
};

function MultiProjectIssuesTable({
  theme,
  loading,
  rowData,
  sourceManagement,
  AGrid,
  statusBadgeData,
  selectedStatuses,
  onToggleStatus,
  isBacklogMode = false,
}) {
  const gridApiRef = useRef(null);
  const aGridRef = useRef(null);
  const columnSelectorRef = useRef(null);
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);

  const colorMap = useMemo(() => createColorMapping(statusBadgeData), [statusBadgeData]);

  const handleFilterIconClick = useCallback((field) => {
    const floatingFilter = gridApiRef.current?.getFloatingFilterComponent(field);
    floatingFilter?.getGui()?.focus();
  }, []);

  const sampleCustomFields = useMemo(
    () =>
      rowData.reduce((acc, row) => ({ ...acc, ...(row.customFieldsByName || {}) }), {}),
    [rowData],
  );

  const fullColumnDefs = useMemo(
    () =>
      jiraColumnDefs(
        handleFilterIconClick,
        sampleCustomFields,
        sourceManagement,
        false,
        'SPRINT OUTCOMES',
        false,
        true,
      ),
    [handleFilterIconClick, sampleCustomFields, sourceManagement],
  );

  const [visibleColumns, setVisibleColumns] = useState(() =>
    fullColumnDefs.filter((col) => !col.optional).map((col) => col.field),
  );

  const allColumns = useMemo(
    () =>
      fullColumnDefs.filter(
        (col) => col.allowToggle && (!isBacklogMode || col.field !== 'sprintOutcome'),
      ),
    [fullColumnDefs, isBacklogMode],
  );

  const displayVisibleColumns = useMemo(() => {
    if (isBacklogMode) {
      return visibleColumns.filter((field) => field !== 'sprintOutcome');
    }
    return visibleColumns;
  }, [isBacklogMode, visibleColumns]);

  const columnDefsWithApi = useMemo(
    () =>
      fullColumnDefs.map((colDef) => ({
        ...colDef,
        floatingFilterComponentParams: {
          ...(colDef.floatingFilterComponent === SearchFloatingFilter
            ? { gridApi: gridApiRef.current }
            : {}),
        },
      })),
    [fullColumnDefs],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      tooltipShowDelay: 0,
      floatingFilterComponent: SearchFloatingFilter,
      floatingFilterComponentParams: {
        gridApi: gridApiRef.current,
      },
    }),
    [],
  );

  const onJiraGridApiReady = useCallback((api) => {
    gridApiRef.current = api;
    api.onFilterChanged();
  }, []);

  const handleColumnToggle = (field) => {
    const isCurrentlyVisible = displayVisibleColumns.includes(field);
    const canHideColumn = displayVisibleColumns.length > 1;
    const willBeVisible = !isCurrentlyVisible;
    const newVisibleColumns = isCurrentlyVisible
      ? canHideColumn
        ? visibleColumns.filter((f) => f !== field)
        : visibleColumns
      : [...visibleColumns, field];
    setVisibleColumns(newVisibleColumns);
    if (gridApiRef.current?.columnApi) {
      gridApiRef.current.columnApi.setColumnVisible(field, willBeVisible);
    }
  };

  const handleColumnsClearAll = useCallback(() => {
    const requiredColumns = fullColumnDefs
      .filter((col) => !col.optional && (!isBacklogMode || col.field !== 'sprintOutcome'))
      .map((col) => col.field);
    setVisibleColumns(requiredColumns);
    if (gridApiRef.current?.columnApi) {
      allColumns.forEach((col) => {
        gridApiRef.current.columnApi.setColumnVisible(
          col.field,
          requiredColumns.includes(col.field),
        );
      });
    }
  }, [allColumns, fullColumnDefs, isBacklogMode]);

  const onFilterChanged = () => {
    gridApiRef.current?.onFilterChanged();
  };

  return (
    <div className="p-3 bg-[#FFFFFF] dark:bg-[#182433] dark:text-[#C8C8C8] text-[#24527A] rounded-lg shadow-[0_1px_20px_rgba(0,0,0,0.1)] dark:shadow-md border border-[#D1E2F0] dark:border-slate-700 w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {statusBadgeData.length > 0 && (
          <StatusBadges
            statusData={statusBadgeData}
            selectedStatuses={selectedStatuses}
            onToggle={onToggleStatus}
            colorMap={colorMap}
          />
        )}
        <div className="ml-auto relative z-10">
          <DropdownButton
            variant="icon"
            type="columns"
            title="Add Column"
            icon={
              <span className="inline-flex items-center justify-center w-full h-full text-[#066FD1]">
                <Plus size={18} strokeWidth={2.5} />
              </span>
            }
            options={allColumns.map((col) => ({
              value: col.field,
              label: col.headerName,
              checked: displayVisibleColumns.includes(col.field),
              disabled: displayVisibleColumns.length === 1 && displayVisibleColumns.includes(col.field),
            }))}
            onSelect={handleColumnToggle}
            onClearAll={handleColumnsClearAll}
            isOpen={isColumnSelectorOpen}
            setIsOpen={setIsColumnSelectorOpen}
            reference={columnSelectorRef}
            showSearch
          />
        </div>
      </div>

      {loading && rowData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          Loading issues...
        </div>
      ) : !loading && rowData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500">
          No issues found for the selected filters.
        </div>
      ) : (
        <AGrid
          ref={aGridRef}
          rowData={rowData}
          columnDefs={columnDefsWithApi.filter((col) => displayVisibleColumns.includes(col.field))}
          defaultColDef={defaultColDef}
          onApiReady={onJiraGridApiReady}
          onFilterChanged={onFilterChanged}
          theme={theme}
          height="500px"
          initialPageSize={10}
        />
      )}
    </div>
  );
}

MultiProjectIssuesTable.propTypes = {
  theme: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  rowData: PropTypes.arrayOf(PropTypes.object).isRequired,
  sourceManagement: PropTypes.string,
  AGrid: PropTypes.elementType.isRequired,
  statusBadgeData: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  isBacklogMode: PropTypes.bool,
};

export default memo(MultiProjectIssuesTable);
